import React, { Component } from "react";
import { withRouter, RouteComponentProps } from "react-router-dom";
import * as Yup from "yup";

// Styles
import "./GameCategories.component.scss";

// Components
import { Components } from "@webtech/webtech-ui-react/build/webtech-ui";
import Form from "../../../../../utils/forms/components/Form";
import { FormGridField } from "../../../../../utils/forms/components/FormGrid";
import { Dialog } from "../../../../../utils/Dialog/Dialog.component";
import { SubsidiaryUtils } from "../../../../../utils/Subsidiary/SubsidiaryUtils";
import { GameNavigation } from "../GameNavigation/GameNavigation.component";

// Store
import { ActionCreator } from "../../../../../actions/actions";
import { connect } from "react-redux";
import { gamesStoreSelectors } from "../../../store/store";
import { GamesActionCreators } from "../../../actions/actionCreators";
import {
    isAdmin,
    canAddGameCategories,
    canUpdateGameCategories,
    canDeleteGameCategories
} from "../../../../../utils/Authorization/authorization";

export interface GameCategoriesProps extends RouteComponentProps {
    getGameCategories: ActionCreator;
    getSubsidiaries: ActionCreator;
    saveGameCategory: ActionCreator;
    deleteGameCategory: ActionCreator;
    gameCategories: any[];
    gameCategoriesLoading: boolean;
    subsidiaries: any[];
    subsidiariesLoading: boolean;
}

export interface GameCategoryFormValues {
    name: string;
    subsidiaries: number[];
}

export interface GameCategoriesState {
    editing: any;
    formOpen: boolean;
    form: GameCategoryFormValues;
    formIsValid: boolean;
    categoryToDelete: any;
}

const EMPTY_FORM: GameCategoryFormValues = { name: "", subsidiaries: [] };

const categoryFields = (subsidiaries: any[], canRename: boolean): FormGridField[] => [
    {
        name: "name",
        fieldType: "input",
        fieldOptions: {
            id: "game-category-name",
            placeholder: "Name",
            inputType: "text",
            label: "Name",
            disabled: !canRename
        },
        validation: Yup.string()
            .required("Field is required.")
            .max(50, "Max. 50 characters."),
        columnClass: "col-12",
        formSpacing: false
    },
    {
        name: "subsidiaries",
        fieldType: "select",
        fieldOptions: {
            id: "game-category-subsidiaries",
            noValueLabel: "Choose subsidiaries",
            label: "Available In",
            items: SubsidiaryUtils.subsidiarySelectItems(subsidiaries),
            multipleSelection: true
        },
        validation: Yup.array(),
        columnClass: "col-12",
        formSpacing: true
    }
];

export class GameCategories extends Component<
    GameCategoriesProps,
    GameCategoriesState
> {
    state: GameCategoriesState = {
        editing: null,
        formOpen: false,
        form: { ...EMPTY_FORM },
        formIsValid: false,
        categoryToDelete: null
    };

    componentDidMount() {
        this.props.getGameCategories();
        this.props.getSubsidiaries();
    }

    openCreate = () => {
        this.setState({
            editing: null,
            formOpen: true,
            form: { ...EMPTY_FORM },
            formIsValid: false
        });
    };

    openEdit = category => {
        this.setState({
            editing: category,
            formOpen: true,
            form: {
                name: category.name,
                subsidiaries: [...(category.subsidiary_ids || [])]
            },
            formIsValid: true
        });
    };

    closeForm = () => {
        this.setState({
            editing: null,
            formOpen: false,
            form: { ...EMPTY_FORM },
            formIsValid: false
        });
    };

    onFormChange = ({ values, isValid }) => {
        this.setState({ form: values, formIsValid: isValid });
    };

    save = () => {
        if (!this.state.formIsValid) {
            return;
        }

        this.props.saveGameCategory(
            {
                ...(this.state.editing || {}),
                name: (this.state.form.name || "").trim()
            },
            this.state.form.subsidiaries || []
        );

        this.closeForm();
    };

    onDelete = category => {
        this.setState({ categoryToDelete: category });
    };

    onDeleteDialogClose = () => {
        this.setState({ categoryToDelete: null });
    };

    onDeleteDialogConfirm = () => {
        this.props.deleteGameCategory(this.state.categoryToDelete.id);
        this.setState({ categoryToDelete: null });
    };

    renderMarkets = category => {
        const ids = category.subsidiary_ids || [];
        const subsidiaries = (this.props.subsidiaries || []).filter(s =>
            ids.includes(s.id)
        );

        if (subsidiaries.length === 0) {
            return "-";
        }

        return subsidiaries.map(subsidiary => (
            <span className="subsidiary-icon tooltip-icon" key={subsidiary.id}>
                <span className="tooltip-text">{subsidiary.description}</span>
                {SubsidiaryUtils.subsidiaryIcon(subsidiary)}
            </span>
        ));
    };

    renderCategories = () => {
        const isLoading =
            this.props.gameCategoriesLoading || this.props.subsidiariesLoading;

        if (isLoading) {
            return (
                <div className="game-categories-loading">
                    <Components.LoadingRipple width="100px" height="100px" />
                </div>
            );
        }

        const categories = this.props.gameCategories || [];

        if (categories.length === 0) {
            return (
                <div className="game-categories-empty">
                    No game categories found.
                </div>
            );
        }

        const canEdit = isAdmin() || canUpdateGameCategories();
        const canDelete = isAdmin() || canDeleteGameCategories();

        return (
            <div className="game-categories-list">
                {categories.map(category => (
                    <div className="game-category-card" key={category.id}>
                        <div className="game-category-card-body">
                            <label>Category</label>
                            <h2>{category.name}</h2>
                            <label>Available In</label>
                            <div className="category-subsidiaries">
                                {this.renderMarkets(category)}
                            </div>
                        </div>

                        <div className="game-category-card-footer">
                            {canEdit && (
                                <button
                                    aria-label={`Edit ${category.name}`}
                                    className="tooltip-icon"
                                    onClick={() => this.openEdit(category)}
                                >
                                    <span className="tooltip-text">Edit</span>
                                    <i className="icon-pencil-alt-light" />
                                </button>
                            )}
                            {canDelete && (
                                <button
                                    aria-label={`Delete ${category.name}`}
                                    className="tooltip-icon"
                                    onClick={() => this.onDelete(category)}
                                >
                                    <span className="tooltip-text">Delete</span>
                                    <i className="icon-trash-alt-light" />
                                </button>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    render() {
        const canCreate = isAdmin() || canAddGameCategories();

        return (
            <div id="game-categories" className="content">
                <GameNavigation />
                <div className="container">
                    <div className="game-categories-header">
                        <h2>Game Categories</h2>
                        {canCreate && (
                            <button
                                className="red-btn"
                                disabled={this.props.subsidiariesLoading}
                                onClick={this.openCreate}
                            >
                                New Category
                            </button>
                        )}
                    </div>
                    {this.renderCategories()}
                </div>

                <Dialog
                    open={this.state.formOpen}
                    onClose={this.closeForm}
                    onConfirm={this.save}
                    confirmLabel={this.state.editing ? "Save" : "Create"}
                    validSubmit={this.state.formIsValid}
                    addedClassName="game-category-form-dialog"
                >
                    {this.state.formOpen && (
                        <div className="game-category-form">
                            <h3>
                                {this.state.editing
                                    ? "Edit Category"
                                    : "New Category"}
                            </h3>
                            <Form
                                key={
                                    this.state.editing
                                        ? `edit-${this.state.editing.id}`
                                        : "new"
                                }
                                fields={categoryFields(
                                    this.props.subsidiaries || [],
                                    isAdmin()
                                )}
                                initialValues={this.state.form}
                                onChange={this.onFormChange}
                            />
                        </div>
                    )}
                </Dialog>

                <Dialog
                    open={this.state.categoryToDelete !== null}
                    onClose={this.onDeleteDialogClose}
                    onConfirm={this.onDeleteDialogConfirm}
                >
                    You are about to delete a Game Category. It will be removed
                    from every market it is available in. Are you sure?
                </Dialog>
            </div>
        );
    }
}

const mapStateToProps = state => ({
    gameCategories: gamesStoreSelectors.gameCategories(state),
    gameCategoriesLoading: gamesStoreSelectors.gameCategoriesLoading(state),
    subsidiaries: gamesStoreSelectors.subsidiaries(state),
    subsidiariesLoading: gamesStoreSelectors.subsidiariesLoading(state)
});

const mapDispatchToProps = {
    getGameCategories: GamesActionCreators.getGameCategories,
    getSubsidiaries: GamesActionCreators.getSubsidiaries,
    saveGameCategory: GamesActionCreators.saveGameCategory,
    deleteGameCategory: GamesActionCreators.deleteGameCategory
};

export default withRouter(
    connect(
        mapStateToProps,
        mapDispatchToProps
    )(GameCategories)
);
